<template>
    <form :action="formAction" method="GET" @submit.prevent="saveToStore">
        <input name="airportA" :value="location.id" type="hidden" />
        <input name="hotelToParking" value="false" type="hidden" />
        <input name="category" value="airporthotel" type="hidden" />
        <input name="params" value="[]" type="hidden" />
        <input name="terminal" value="" type="hidden" />
        <input name="timeA" value="12:00" type="hidden" />
        <input name="timeB" value="12:00" type="hidden" />
        <input name="room1" :value="roomType" type="hidden" />
        <input name="agent" :value="blok.agent_code" type="hidden" />
        <input
            :name="onReturn ? 'dateB' : 'dateA'"
            :value="new Date(quoteStore.getAllData.fromDate).toISOString().split('T')[0]"
            type="hidden"
        />
        <input
            :name="onReturn ? 'dateA' : 'dateB'"
            :value="new Date(quoteStore.getAllData.toDate).toISOString().split('T')[0]"
            type="hidden"
        />

        <input name="promo" :value="discountValue" type="hidden" />
        <fieldset>
            <!-- Begin: Hotel Quote Form - Form - Airport Search -->
            <SearchInput
                icon="plane-fill"
                name="search"
                :placeholder="t('quoteForm.search')"
                :items="groupedItems"
                :value="location.name"
                :country="locale"
                :validation-errors-count="validationErrorsCount"
                @update:value="updateLocation"
            />

            <!-- Begin: Hotel Quote Form - Form - Before/After Flight -->
            <div class="input-group">
                <label for="beforeFlight" class="input-radio">
                    <input
                        id="beforeFlight"
                        v-model="onReturn"
                        type="radio"
                        name="return"
                        :value="false"
                        :disabled="!isMounted"
                    />
                    {{ t('quoteForm.hotel.beforeFlight') }}
                </label>
                <label for="afterFlight" class="input-radio">
                    <input
                        id="afterFlight"
                        v-model="onReturn"
                        type="radio"
                        name="return"
                        :value="true"
                        :disabled="!isMounted"
                    />
                    {{ t('quoteForm.hotel.afterFlight') }}
                </label>
            </div>
            <!-- End: Hotel Quote Form - Form - Before/After Flight -->

            <!-- Begin: Hotel Quote Form - Form - From Date/Time -->
            <div class="input-group">
                <label for="fromDateInput" class="input-label">
                    {{ onReturn ? t('quoteForm.hotel.parkingFrom') : t('quoteForm.hotel.nightStay') }}:
                </label>
                <div class="input-row">
                    <DateInput
                        icon="calendar-2-line"
                        name="from"
                        direction="from"
                        :anchor="blok.order"
                        :min-date="currentDate()"
                        :value="quoteStore.getAllData.fromDate ? quoteStore.getAllData.fromDate : null"
                        :placeholder="t('quoteForm.date')"
                        @update:value="
                            (value: any) => quoteStore.updateQuoteFormFrom(value, quoteStore.getAllData.fromTime)
                        "
                    />
                </div>
            </div>
            <!-- End: Hotel Quote Form - Form - From Date/Time -->

            <!-- Begin: Hotel Quote Form - Form - To Date/Time -->
            <div class="input-group">
                <label for="toDateInput" class="input-label">
                    {{ onReturn ? t('quoteForm.hotel.hotelStayNight') : t('quoteForm.hotel.collectCar') }}:
                </label>
                <div class="input-row">
                    <DateInput
                        icon="calendar-2-line"
                        name="to"
                        direction="to"
                        :anchor="blok.order"
                        :min-date="quoteStore.getAllData.fromDate ? quoteStore.getAllData.fromDate : currentDate()"
                        :value="quoteStore.getAllData.toDate ? quoteStore.getAllData.toDate : null"
                        :placeholder="t('quoteForm.date')"
                        @update:value="
                            (value: any) => {
                                quoteStore.updateQuoteFormTo(value, quoteStore.getAllData.toTime);
                            }
                        "
                    />
                </div>
            </div>
            <!-- End: Hotel Quote Form - Form - To Date/Time -->

            <!-- Begin: Hotel Quote Form - Form - Room Type -->
            <div class="input-group">
                <label for="roomTypeInput" class="input-label"> {{ t('quoteForm.hotel.roomType') }}: </label>
                <div class="input-row">
                    <div class="input-group">
                        <NuxtIcon name="ri:hotel-bed-fill" class="input-icon-prepend" />
                        <select id="roomTypeInput" v-model="roomType" name="roomTypeInput" :disabled="!isMounted">
                            <template v-for="(roomTypeOption, index) in roomTypeOptions" :key="index">
                                <option :value="roomTypeOption.value">
                                    {{ roomTypeOption.label }}
                                </option>
                            </template>
                        </select>
                    </div>
                </div>
            </div>
            <!-- End: Hotel Quote Form - Form - Room Type -->

            <!-- Begin: Quote Form - Form - Coupon -->
            <div v-show="blok.show_discount" class="input-group">
                <NuxtIcon name="ri:coupon-2-line" class="input-icon-prepend" />
                <input
                    id="discountInput"
                    type="text"
                    name="discount"
                    :disabled="!isMounted"
                    :placeholder="t('quoteForm.discount')"
                    :value="discountValue"
                    @input="updateDiscount"
                />
            </div>
            <!-- End: Quote Form - Form - Coupon -->

            <ComparePrices
                :type-compare="location.code ? 'submit' : 'button'"
                class-compare="submit right-icon text-lg font-semibold"
                @click="checkAirport"
            />
        </fieldset>
    </form>
</template>

<script setup lang="ts">
import { useQuoteStore } from '~/store/quote';
import DateInput from '~/components/Form/DateInput.vue';
import SearchInput from '~/components/Form/SearchInput.vue';
import type { IL4QuoteFormHotels } from '~/types/components/L4QuoteFormHotels';
import { differenceInDays } from '~/utils/helpers';

const { blok } = defineProps<{ blok: IL4QuoteFormHotels }>();

// Const variable for access to runtime config
const config = useRuntimeConfig();

// Constant variable for access to route information
const route = useRoute();

// Constant variable for access to quote store
const quoteStore = useQuoteStore();

// Constant Variable for access to locale & translations
const { t, locale } = useI18n();

const isMounted = ref(false);

const onReturn = ref<boolean>(false);
const today = ref(new Date());
//in case if user open the page and leave it to the next day, the date should be updated
const currentDate = () => {
    if (differenceInDays(today.value, new Date()) > 0) {
        today.value = new Date();
    }

    return today.value;
};
// Reference to selectedLocation which is updated by FormSearch component
const location: Ref<{ name: string; code: string; id: string }> = ref({
    name: blok.airport.name,
    code: blok.airport.content ? blok.airport?.content?.code?.toUpperCase() : blok.airport?.code?.toUpperCase(),
    id: blok.airport.content ? blok.airport?.content?.airport_id : blok.airport?.airport_id,
});

if (route.query.code) {
    quoteStore.updateQuoteFormDiscount(route.query.cod as string);
}

const discountChanged = ref(false);

const updateDiscount = (event: InputEvent) => {
    discountChanged.value = true;
    quoteStore.updateQuoteFormDiscount((event.target as HTMLInputElement).value);
};

const discountValue = computed(() =>
    quoteStore.getAllData.discount === '' && !discountChanged.value
        ? blok.default_discount
        : quoteStore.getAllData.discount
);

const roomType = ref<number>(1);

const roomTypeOptions = [
    { value: '1', label: 'Single', disabled: false },
    { value: '2', label: 'Double', disabled: false },
    { value: '3', label: 'Twin', disabled: false },
    { value: '4', label: 'Triple (3 adults)', disabled: false },
    { value: '5', label: 'Family of 3', disabled: false },
    { value: '6', label: 'Family of 4', disabled: false },
    { value: '7', label: 'Family of 5', disabled: false },
    { value: '1001', label: 'Family of 6', disabled: false },
    { value: '1005', label: 'Family of 7', disabled: false },
    { value: '1008', label: 'Executive Single', disabled: false },
    { value: '1003', label: 'Executive Double', disabled: false },
    { value: '1004', label: 'Honeymoon', disabled: false },
    { value: '1009', label: 'Quad (4 adults)', disabled: false },
    { value: '1010', label: 'Exec.Family 4', disabled: false },
];

// Computed variable for the form action. Will update based on selectedLocation.
const formAction = computed(() => (location.value.code ? `/${locale.value}/booking-quote/airporthotel-results` : ''));

let groupedItems: unknown[] = [];

const airportDropdownList: any = await useAsyncStoryblok(`l4/global/dropdowns/airports`, {
    version: config?.public?.STORYBLOK_VERSION === 'published' ? 'published' : 'draft',
    language: getLanguageForLocale(locale.value),
    resolve_relations: ['AirportList.country'],
}).then((data) => {
    const airports = [];
    data.value?.content?.List.map((countryAirports) => {
        countryAirports.List.map((airport) => {
            airports.push({
                code: airport?.code,
                name: airport?.name,
                country: {
                    slug: countryAirports.country?.content?.code,
                    name: countryAirports.country?.content?.name,
                },
                id: airport?.airportId,
            });
        });
    });

    return airports.filter(
        (item, index, array) => index === array.findIndex((findItem) => findItem?.code === item?.code)
    );
});

let filteredResults = airportDropdownList;

if (config?.public?.STORYBLOK_VERSION === 'published') {
    filteredResults = filteredResults.filter((item) => {
        return item.code !== 'PTE';
    });
}

// Re-arrange the areas to be a child of their countries
groupedItems = Object.values(groupItemBy([...new Set(filteredResults)], 'country.slug'));
// Validation
const validationErrorsCount = ref(0);

const checkAirport = () => {
    if (!location.value.code) {
        validationErrorsCount.value++;
    }
};

const updateLocation = (value) => {
    location.value = {
        code: value.code,
        name: value.name,
        id: value.id,
    };
    validationErrorsCount.value = 0;
};

// Save to store only after submitting form to search results, for reusing after 'go back' action
// When a user navigates through Nuxt pages, Pinia doesn't update.
const saveToStore = (e) => {
    quoteStore.updateQuoteFormStartDestination({ code: location.value.code, name: location.value.name });
    quoteStore.updateQuoteFormReturn(onReturn.value);
    e.target.submit();
};

onMounted(() => {
    isMounted.value = true;
});
</script>
